
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/hooks/useAuth';
import { postFunctionData } from '@/utils/supabaseFunctions';

interface StripeBalance {
  available: number;
  pending: number;
  currency: string;
  isTestMode: boolean;
  isConnected?: boolean;
  error?: string;
}

export const useStripeBalance = () => {
  const { user } = useAuth();
  const organizationId = user?.user_metadata?.organization_id;

  return useQuery({
    queryKey: ['stripeBalance', organizationId],
    queryFn: async (): Promise<StripeBalance> => {
      try {
        if (!organizationId) {
          console.log('No organization ID available, returning default balance object');
          return {
            available: 0,
            pending: 0,
            currency: 'usd',
            isTestMode: false,
            isConnected: false
          };
        }
        
        // Use our utility for better error handling and authentication
        const data = await postFunctionData<StripeBalance>(
          'get-stripe-balance', 
          { organizationId }
        );
        
        // Ensure isConnected is properly set based on the response
        return {
          ...data,
          isConnected: data.isConnected !== undefined ? data.isConnected : !data.error
        };
      } catch (error) {
        console.error('Error fetching Stripe balance:', error);
        // Return a default object instead of throwing to prevent UI errors
        return {
          available: 0,
          pending: 0,
          currency: 'usd',
          isTestMode: false,
          isConnected: false,
          error: error instanceof Error ? error.message : 'Failed to fetch balance data'
        };
      }
    },
    enabled: !!organizationId,
    // Modify cache settings to prevent excessive API calls
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false,
    retry: 1, // Only retry once to avoid excessive failed calls
    refetchInterval: false, // Disable automatic refetching
    refetchOnMount: true // Only fetch once when component mounts
  });
};
